/** Brand name — always shown in English regardless of locale. */
export const GAME_DISPLAY_NAME = 'Ancient Egypt Wilds';

/**
 * Default English copy for slot-casino (API fallback + translator handoff).
 * Keys are stable identifiers; values match current on-screen English.
 *
 * When adding a new key here, also:
 * 1. Run `node scripts/export-translation-tsv.mjs` (updates English TSV exports).
 * 2. Add a matching row to `localization/translation-matrix-excel.tsv` (all locale columns).
 *
 * @type {Record<string, string>}
 */
export const slotCasinoStringsEn = {
  GAME_TITLE: GAME_DISPLAY_NAME,
  HEADER_SUBTITLE: 'CHAIN WINS · HIGH-VOL ACTION',
  LOADING_GAME: 'Loading...',
  START_SCREEN_CONTINUE: 'Continue',
  START_SCREEN_FEATURE_LABEL: 'Expanding Wilds',
  START_SCREEN_MAX_WIN: 'Max win: {{maxWin}}',
  START_SCREEN_RTP: 'RTP {{rtp}}%',
  START_SCREEN_TAP_HINT: 'Tap anywhere to continue',
  START_SCREEN_CLICK_HINT: 'Click Continue to start playing',
  START_SCREEN_PREVIEW_BADGE: 'Feature preview',
  BALANCE: 'Balance',
  LAST_WIN: 'Last win',
  WIN: 'Win',
  TOTAL_BET: 'Total bet',
  LINES: 'LINES',
  LINE: 'Line',
  BET: 'Bet',
  SPIN: 'Spin',
  AUTO: 'Auto',
  MAX: 'Max',
  OFF: 'Off',
  ON: 'On',
  COLLECT: 'Collect',
  STOP: 'Stop',
  STOP_ALL: 'Stop all',
  COLLECT_WIN: 'Collect win',
  BET_OPTIONS: 'Bet options',
  AUTOPLAY_SETTINGS: 'Autoplay settings',
  PLEASE_PLACE_YOUR_BET: 'Please place your bet',
  PLEASE_PLACE_YOUR_BETS: 'Please place your bets',
  GOOD_LUCK: 'Good luck!',
  /** Win-line field below reels while paylines cycle (see INFO_FIELD_WINLINE). */
  WIN_LINE_PAYOUT: 'Line {{line}} — {{amount}}',
  WIN_SYMBOL_PAYOUT: 'Win — {{amount}}',
  HOLD_FOR_TURBO_SPIN: 'Hold for turbo spin',
  SOUND: 'Sound',
  SETTINGS_LABEL_QUICK_SPIN: 'Quick Spin',
  GAME_SETTINGS: 'Game settings',
  EXIT_TO_LOBBY: 'Exit to lobby',
  IDLE_TIMEOUT_RELOAD: 'You were inactive for a while. The game will reload.',
  ROTATE_DEVICE_TITLE: 'Please rotate your device',
  ROTATE_DEVICE_BODY:
    'This game is designed for portrait mode on mobile. Turn your phone vertically to continue playing.',
  CONNECTION_LOST_TITLE: 'Connection lost',
  CONNECTION_LOST_BODY:
    'Your network was interrupted. Please reload the game to continue.',
  CONNECTION_RESTORED: 'Connection restored',
  DUPLICATE_SESSION_TITLE: 'Session ended',
  DUPLICATE_SESSION_BODY:
    'This game was opened in another window or tab. Close the other session or reload to continue here.',
  HUB_ACTION_REJECTED: 'Spin was rejected. Please try again.',
  HUB_ACTION_INSUFFICIENT_BALANCE: 'Insufficient balance for this bet.',
  HUB_ACTION_TOKEN_REJECTED: 'Your session expired. Please reload the game.',
  HUB_ACTION_GAME_UNAVAILABLE: 'This game is unavailable. Please reload or try again later.',
  HUB_ACTION_SERVER_FAILURE: 'Something went wrong. Please try again.',
  RELOAD_PAGE: 'Reload game',
  INFO: 'Information',
  QUICK_SPIN: 'Quick spin',
  AUTOPLAY: 'Autoplay',
  START: 'Start',
  SPINS: 'Spins',
  IF_BALANCE_DECREASES_BY: 'If balance decreases by',
  IF_SINGLE_WIN_EXCEEDS: 'If single win exceeds',
  IF_BALANCE_INCREASES_BY: 'If balance increases by',
  PAYLINES_AND_RULES: 'Paylines and rules',
  GAME_INTERRUPTION: 'Game interruptions',
  FULL_GAME_RECOVERY: 'Full game recovery',
  CANCELLATION: 'Cancellation',
  RESPONSIBLE_GAMING: 'Responsible gaming',
  HOW_TO_USE_AUTOPLAY_FEATURE: 'How to use autoplay feature',
  RETURN_TO_PLAYER: 'Return to player',

  INFO_INTRO_TITLE: 'Introduction',
  INFO_INTRO_BODY: `{{gameTitle}} is a 5-reel, 10-line fixed video slot. The game has 11 symbols: 8 regular symbols, 1 Expanding Wild, and 2 Scatters. Line wins are paid left to right. Scatter wins are paid separately.`,
  INFO_WILD_HEADING: 'WILD',
  INFO_WILD_LINE1: 'On 2nd, 3rd and 4th reels and',
  INFO_WILD_LINE2: 'substitutes for all symbols on',
  INFO_WILD_LINE3: 'the same reel except for ',
  INFO_WILD_LINE4: 'and ',
  INFO_SCATTER_HEADING: 'SCATTER',
  INFO_SCATTER_STAR_INTRO: 'Anywhere on 1st,\n3rd and 5th reels:',

  INFO_HEADING_PAYLINES_AND_RULES: 'PAYLINES AND RULES',
  INFO_RULES_PARA1:
    'All pays are for combinations of a kind. All pays are left to right on adjacent reels, on selected lines, beginning with the leftmost reel, except Scatters.',
  INFO_RULES_PARA2:
    'Scatter wins are added to the payline wins. Highest payline and/or Scatter wins only paid. Line wins are multiplied by the bet value on the winning line.',
  INFO_RULES_PARA3:
    'Scatter wins are multiplied by the total bet value. Malfunction voids all pays and plays. This game is random and the player\u2019s skill or dexterity does not affect its outcome.',

  INFO_HEADING_HOW_TO_BET: 'HOW TO BET',
  INFO_HOW_TO_BET_BULLET1: '\u2022 A bet is made by pressing the \u201CStart\u201D button, or any of the Bet buttons.',
  INFO_HOW_TO_BET_BULLET2:
    '\u2022 Through the \u201CMenu\u201D button, the player accesses a screen where they can open the information page.',

  INFO_HEADING_GAME_CONTROLS: 'GAME CONTROLS',
  INFO_FIELDS_INTRO: 'The following fields appear on the screen:',
  INFO_FIELD_BALANCE: '\u2022 \u201CBalance\u201D \u2013 this field displays the players balance;',
  INFO_FIELD_WIN:
    '\u2022 \u201CWin\u201D \u2013 this field displays the total amount won in one rotation of the reels. If the \u201CCollect\u201D button is activated while the animation of the increasing amount is active, the animation stops and the amount won for this rotation of the reels is shown;',
  INFO_FIELD_WINLINE:
    '\u2022 Win Line field \u2013 this field is located below the reels. It shows the line number and the amount the player won from that combination.',
  INFO_BUTTONS_INTRO: 'The following buttons appear on the screen:',

  INFO_TABLE_HDR_BUTTON: 'BUTTON',
  INFO_TABLE_HDR_NAME: 'NAME',
  INFO_TABLE_HDR_DESC: 'DESCRIPTION',

  INFO_CTRL_BET_NAME: 'Bet',
  INFO_CTRL_BET_BODY:
    'When the \u201CBet\u201D button is activated, a bet is made. If an amount had been won earlier, it is automatically added to the player\u2019s balance.',
  INFO_CTRL_STOP_ALL_NAME: 'STOP ALL',
  INFO_CTRL_STOP_ALL_BODY:
    'When the \u201CStop All\u201D button is activated, all reels stop turning at the same time and the button changes to \u201CBet\u201D (if there is no winning combination), or \u201CCollect\u201D (if there is a winning combination). When activating the \u201CStart\u201D and \u201CBet\u201D buttons, the reels start turning and the \u201CBet\u201D buttons turn into \u201CStop All\u201D buttons.',
  INFO_CTRL_COLLECT_NAME: 'COLLECT',
  INFO_CTRL_COLLECT_BODY:
    'When activating the \u201CCollect\u201D button, the increased winning animation stops and the entire winning sum is shown in the \u201CWin\u201D field, and the status of the button turns to \u201CBet\u201D.',
  INFO_CTRL_START_NAME: 'START',
  INFO_CTRL_START_BODY:
    'When activating the \u201CStart\u201D button, the reels start turning. The player can hold the button to activate the Turbo Spin function, which speeds up the rotation of the reels. If an amount had been won earlier, the \u201CStart\u201D button is replaced with the \u201CCollect\u201D button.',
  INFO_CTRL_COLLECT_WIN_NAME: 'COLLECT WIN',
  INFO_CTRL_COLLECT_WIN_BODY:
    'When activating the \u201CCollect Win\u201D button, the entire winning sum is transferred from the \u201CWin\u201D field to the \u201CBalance\u201D field.',
  INFO_CTRL_GAMBLE_NAME: 'Gamble',
  INFO_CTRL_GAMBLE_BODY: 'When activated it opens up the Gamble screen.',
  INFO_CTRL_BET_OPTIONS_NAME: 'BET OPTIONS',
  INFO_CTRL_BET_OPTIONS_BODY:
    'When it opens, the player can choose from different bet options. The bet option currently selected is displayed there.',
  INFO_CTRL_AUTOPLAY_SETTINGS_NAME: 'AUTOPLAY',
  INFO_CTRL_AUTOPLAY_SETTINGS_BODY:
    '\u201CAutoplay settings\u201D is located inside the \u201CSettings\u201D menu. From there the player can define the Autoplay mode conditions.',
  INFO_CTRL_GAME_SETTINGS_NAME: 'SETTINGS',
  INFO_CTRL_GAME_SETTINGS_BODY: '\u201CGame settings\u201D is located inside the \u201CSettings\u201D menu.',
  INFO_CTRL_BET_VALUE_NAME: 'BET VALUE',
  INFO_CTRL_BET_VALUE_BODY:
    'The amount displayed on each of the \u201Cbet value\u201D buttons is the total amount for all paylines active at this moment. When activated, different values are displayed and the player can make their choice.',
  INFO_CTRL_AUTOPLAY_MODE_NAME: 'AUTOPLAY',
  INFO_CTRL_AUTOPLAY_MODE_BODY:
    'When the \u201CAutoplay\u201D button is activated, the game goes into Autoplay mode.',
  INFO_CTRL_STOP_AUTO_NAME: 'STOP AUTOPLAY',
  INFO_CTRL_STOP_AUTO_BODY:
    'When the \u201CStop Auto\u201D button is activated, the \u201CAutoplay\u201D game mode stops. This button is active only when the game is in Autoplay mode.',
  INFO_CTRL_FAST_PLAY_NAME: 'FAST PLAY',
  INFO_CTRL_FAST_PLAY_BODY:
    'This button adjusts the stopping speed of the reels. The game supports 3 different stopping speed levels.',
  INFO_CTRL_SOUND_CONTROL_NAME: 'SOUND CONTROL',
  INFO_CTRL_SOUND_CONTROL_BODY:
    'Upon activation of the button, the sound of the game is switched on/off, depending on its preceding condition.',
  INFO_CTRL_SCREEN_SIZE_NAME: 'SCREEN SIZE CONTROL',
  INFO_CTRL_SCREEN_SIZE_BODY:
    'Upon activation of the button, the screen either expands or decreases its size, depending on its current state. The button is enabled on desktop devices only.',
  INFO_CTRL_MENU_NAME: 'MENU',
  INFO_CTRL_MENU_BODY: 'Upon clicking on the \u201CMenu\u201D button, the player enters the \u201CSettings\u201D menu.',
  INFO_CTRL_CLOSE_NAME: 'CLOSE',
  INFO_CTRL_CLOSE_BODY: 'When activated, this button returns the player to the main screen.',
  INFO_CTRL_EXIT_NAME: 'EXIT',
  INFO_CTRL_EXIT_BODY:
    'When activating this button, the player leaves the game. The button is inactive when the reels are rotating, when the player has won and they have the option to gamble, in Gamble mode, and during Free Spins and/or other Game Features (if such are present in the game). If a sum is won during activation of the button, the amount won is added to the player\u2019s balance and the game is closed. The player cannot gamble this last amount won any further.',

  INFO_TABLE_FOOTER_CLOCK: 'At the bottom of the game screen, the player can also see the game number and the clock.',
  INFO_TABLE_FOOTER_SPACE_KEY:
    'All values in the above table are for illustrative purposes only. If the player uses a desktop or laptop machine, pressing the \u201CSpace\u201D key on their keyboard may result in starting or stopping the rotation of the wheels, depending on the current state of the game. It can also cease the animation of the increased winning if it is active.',

  INFO_HEADING_RULES: 'RULES',
  INFO_RULE_BULLET_1:
    '\u2022 Scatter awards are independent from payline awards and are also added to the total amount paid.',
  INFO_RULE_BULLET_2: '\u2022 Simultaneous wins on different paylines are added.',
  INFO_RULE_BULLET_3: '\u2022 A malfunction voids all payments and playing.',
  INFO_RULE_BULLET_SPACE:
    '\u2022 On desktop and laptop computers, pressing the \u201CSpace\u201D key starts or stops a spin, or dismisses active win celebrations.',
  INFO_RULE_BULLET_SPACE_BUY_BONUS:
    '\u2022 On desktop and laptop computers, pressing the \u201CSpace\u201D key starts or stops a spin, or dismisses active win celebrations. Space does nothing while the buy-bonus window is open.',
  INFO_RULE_BULLET_BUY_BONUS:
    '\u2022 Buy Bonus purchases a spin with 1, 2, or 3 expanding wilds. Guaranteed Win is optional and raises the price.',
  BUY_BONUS: 'Buy Bonus',
  BUY_BONUS_BTN_LINE1: 'BUY',
  BUY_BONUS_BTN_LINE2: 'BONUS',
  BUY_BONUS_MODAL_TITLE: 'Buy Bonus',
  BUY_BONUS_SUBTITLE: 'Choose 1, 2, or 3 expanding wild reels for the next spin.',
  BUY_BONUS_STAKE_MULTIPLIER: '{{mul}}× stake',
  BUY_BONUS_CTA: 'Buy & spin',
  BUY_BONUS_CTA_SUB: '{{price}}',
  BUY_BONUS_CTA_SUB_DETAIL: '{{wild}} · {{price}}',
  BUY_BONUS_GUARANTEED_ENABLED: 'Guaranteed win: Enabled',
  BUY_BONUS_GUARANTEED_DISABLED: 'Guaranteed win: Disabled',
  BUY_BONUS_1_WILD: '1 wild reel',
  BUY_BONUS_N_WILDS: '{{n}} wild reels',
  INFO_RULE_BULLET_MINMAX: '\u2022 Min/Max Bet: {{min}} / {{max}}',
  INFO_RULE_BULLET_MAXWIN: '\u2022 Max Win: {{maxWin}}',

  INFO_HEADING_GAME_INTERRUPTIONS: 'GAME INTERRUPTIONS',
  INFO_HEADING_FULL_GAME_RECOVERY: 'FULL GAME RECOVERY',
  INFO_FULL_RECOVERY_BODY:
    'In the event that the game is interrupted due to loss of connection, when the player accesses the game again, the unfinished game will be automatically resumed at the state it was in at the time immediately before the interruption.',
  INFO_HEADING_CANCELLATION: 'CANCELLATION',
  INFO_CANCELLATION_BODY:
    'In the event that the game is cancelled independently from the player, the moment the cancellation took place is considered to be the end of the game. Any funds accumulated will be automatically returned to the player.',

  INFO_HEADING_RESPONSIBLE_GAMING: 'RESPONSIBLE GAMING',
  INFO_HEADING_HOW_TO_USE_AUTOPLAY: 'HOW TO USE AUTOPLAY FEATURE',
  INFO_AUTOPLAY_USE_BODY:
    'When the \u201CAutoplay\u201D button is pressed, the game goes into automatic mode with a limited range of number of spins. During Autoplay, there will be a countdown of the remaining number of spins inside the \u201CStop auto\u201D button and when it reaches zero, the Autoplay feature will be automatically terminated. At any time, the player can disable the Autoplay feature by pressing the \u201CStop auto\u201D button.',

  INFO_HEADING_RETURN_TO_PLAYER: 'RETURN TO PLAYER',
  INFO_RTP_BODY: 'The average return to Player of the game is 96.34%.',
};
